import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getCheckoutStatus } from "../lib/api";
import { useCart } from "../context/CartContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { trackPurchase } from "../lib/analytics";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", payment_status: "pending", amount_total: 0, currency: "gbp" });
  const { clearCart } = useCart();
  const attempts = useRef(0);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment_status: "unknown" });
      return;
    }
    let cancelled = false;

    // Polling: 500ms × 6 quick attempts, then 2s × 6 slower attempts (~15s total).
    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 12) {
        setState((s) => ({ ...s, status: "timeout" }));
        return;
      }
      attempts.current += 1;
      try {
        const data = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        setState(data);
        if (data.payment_status === "paid") {
          if (!clearedRef.current) {
            clearedRef.current = true;
            clearCart();
            trackPurchase({
              transaction_id: sessionId,
              value: data.amount_total || 0,
            });
          }
          return;
        }
        if (data.status === "expired") return;
        const delay = attempts.current <= 6 ? 500 : 2000;
        setTimeout(poll, delay);
      } catch (e) {
        const delay = attempts.current <= 6 ? 500 : 2000;
        setTimeout(poll, delay);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, clearCart]);

  const paid = state.payment_status === "paid";
  const failed = state.status === "expired" || state.status === "error";
  const timedOut = state.status === "timeout";

  let statusIcon;
  if (paid) {
    statusIcon = <CheckCircle2 className="w-12 h-12 text-[#1a1814]" strokeWidth={1} />;
  } else if (failed) {
    statusIcon = <XCircle className="w-12 h-12 text-[#1a1814]" strokeWidth={1} />;
  } else if (timedOut) {
    statusIcon = <CheckCircle2 className="w-12 h-12 text-[#1a1814]" strokeWidth={1} />;
  } else {
    statusIcon = <Loader2 className="w-12 h-12 animate-spin text-[#1a1814]" strokeWidth={1} />;
  }

  let headline;
  if (paid) {
    headline = "Thank you. Your atelier order is confirmed.";
  } else if (failed) {
    headline = "Something held up.";
  } else if (timedOut) {
    headline = "Your order is being processed.";
  } else {
    headline = "Confirming your order…";
  }

  let detail;
  if (paid) {
    detail = "A confirmation note is on its way to your inbox. Your pieces are being prepared by our small team.";
  } else if (failed) {
    detail = "We couldn't confirm payment. If you were charged, please reach out and we'll resolve immediately.";
  } else if (timedOut) {
    detail = "Your payment has gone through to Stripe. We're finalising the order — a confirmation email will follow shortly. You can safely close this page.";
  } else {
    detail = "We're verifying your payment with Stripe. This usually takes a few seconds.";
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="checkout-success-page">
      <div className="mb-8 flex justify-center">{statusIcon}</div>
      <div className="micro-label text-[#1a1814]/60 mb-4">Order Status</div>
      <h1 className="font-display text-5xl text-[#1a1814] leading-tight" data-testid="checkout-status-headline">
        {headline}
      </h1>
      <p className="text-[#1a1814]/70 mt-6 leading-relaxed">
        {detail}
      </p>
      {state.amount_total > 0 && (
        <div className="mt-8 inline-block border-y border-[#1a1814]/15 py-4 px-10">
          <div className="micro-label text-[#1a1814]/60 mb-1">Total</div>
          <div className="font-display text-3xl" data-testid="checkout-total">{(state.currency || "gbp").toLowerCase() === "gbp" ? "£" : "$"}{state.amount_total.toFixed(2)} {(state.currency || "gbp").toUpperCase()}</div>
        </div>
      )}
      <div className="mt-12 flex justify-center gap-4">
        <Link to="/shop" className="micro-label border border-[#1a1814] text-[#1a1814] px-8 py-4 hover:bg-[#1a1814]/5" data-testid="checkout-continue-shopping">
          Continue Browsing
        </Link>
        <Link to="/" className="micro-label bg-[#1a1814] text-[#f0e9e1] px-8 py-4 hover:bg-[#1a1814]/85" data-testid="checkout-return-home">
          Return Home
        </Link>
      </div>
    </div>
  );
}
